import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
} from 'react';
import axios from '@/utils/axios';
import {
  registerUnauthorizedApiLogoutHandler,
  resetUnauthorizedApiRequestTracking,
} from '@/utils/authFailure';
import { isTokenValid, setSession } from '@/utils/jwt';

import {
  ActionMap,
  AuthState,
  AuthUser,
  DashboardDuoChallengeResult,
  DashboardEmailFallbackSentData,
  DashboardEmailFallbackSentResponse,
  DashboardLoginResponse,
  DashboardLoginResult,
  DashboardUserSessionData,
  JWTContextType,
  UserSessionResponse,
} from '@/features/app';
import { destroyNotificationInterval } from '@/features/notification/slices';

export enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    isSuperAdmin: boolean;
    permissions: string[];
    user: AuthUser;
  };
  [Types.Login]: {
    isSuperAdmin: boolean;
    permissions: string[];
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
};

export type JWTActions =
  ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isSuperAdmin: false,
  isInitialized: false,
  permissions: [],
  user: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isSuperAdmin: action.payload.isSuperAdmin,
        isInitialized: true,
        permissions: action.payload.permissions,
        user: action.payload.user,
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        isSuperAdmin: action.payload.isSuperAdmin,
        permissions: action.payload.permissions,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        isSuperAdmin: false,
        permissions: [],
        user: null,
      };
    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        permissions: [],
        user: action.payload.user,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

const getUserPermissions = (user?: AuthUser): string[] => {
  const permissions = user?.permissions;
  return Array.isArray(permissions) ? permissions : [];
};

const getIsSuperAdmin = (user?: AuthUser): boolean => user?.superadmin === true;

const isDuoChallengeResult = (
  data?: DashboardLoginResponse['data']['data']
): data is DashboardDuoChallengeResult =>
  !!data && 'mfa_required' in data && data.mfa_required === true;

const isUserSessionData = (
  data?: DashboardLoginResponse['data']['data']
): data is DashboardUserSessionData =>
  !!data &&
  'token' in data &&
  typeof data.token === 'string' &&
  'user' in data &&
  !!data.user;

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialAuthState);

  const logout = useCallback(async () => {
    setSession(null);
    resetUnauthorizedApiRequestTracking();
    dispatch({ type: Types.Logout });
    destroyNotificationInterval();
  }, []);

  useEffect(() => registerUnauthorizedApiLogoutHandler(logout), [logout]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        if (accessToken && isTokenValid(accessToken)) {
          setSession(accessToken);
          const {
            data: {
              data: { user },
            },
          } = await axios.get('/account/profile');

          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              isSuperAdmin: getIsSuperAdmin(user),
              permissions: getUserPermissions(user),
              user,
            },
          });
        } else {
          setSession(null);
          resetUnauthorizedApiRequestTracking();
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              isSuperAdmin: false,
              permissions: [],
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        setSession(null);
        resetUnauthorizedApiRequestTracking();
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            isSuperAdmin: false,
            permissions: [],
            user: null,
          },
        });
      }
    };
    initialize();
  }, [dispatch]);

  const completeLogin = ({
    token,
    user,
  }: DashboardUserSessionData): DashboardLoginResult => {
    setSession(token);
    resetUnauthorizedApiRequestTracking();
    dispatch({
      type: Types.Login,
      payload: {
        isSuperAdmin: getIsSuperAdmin(user),
        permissions: getUserPermissions(user),
        user,
      },
    });

    return {
      mfa_required: false,
      token,
      user,
    };
  };

  const login = async (
    email: string,
    password: string,
    page?: string
  ): Promise<DashboardLoginResult> => {
    setSession(null);
    resetUnauthorizedApiRequestTracking();

    const {
      data: {
        data,
      },
    }: DashboardLoginResponse = await axios.post('auth/dashboard/login', {
      email,
      password,
      page,
    });

    if (isDuoChallengeResult(data)) {
      return data;
    }

    if (isUserSessionData(data)) {
      return completeLogin(data);
    }

    throw new Error('Unexpected login response from server.');
  };

  const pollDashboardDuoChallenge = async (
    challengeId: string
  ): Promise<DashboardLoginResult> => {
    const {
      data: { data },
    }: DashboardLoginResponse = await axios.get(
      `/auth/dashboard/duo/challenges/${encodeURIComponent(challengeId)}`
    );

    if (isDuoChallengeResult(data)) {
      return data;
    }

    if (isUserSessionData(data)) {
      return completeLogin(data);
    }

    throw new Error('Unexpected Duo challenge response from server.');
  };

  const requestDuoEmailFallback = async (
    challengeId: string
  ): Promise<DashboardEmailFallbackSentData> => {
    const {
      data: { data },
    }: DashboardEmailFallbackSentResponse = await axios.post(
      `/auth/dashboard/duo/challenges/${encodeURIComponent(challengeId)}/email-fallback`
    );

    return data;
  };

  const verifyDuoEmailFallbackCode = async (
    challengeId: string,
    code: string
  ): Promise<DashboardLoginResult> => {
    const {
      data: { data },
    }: DashboardLoginResponse = await axios.post(
      `/auth/dashboard/duo/challenges/${encodeURIComponent(challengeId)}/email-fallback/verify`,
      { code }
    );

    if (isUserSessionData(data)) {
      return completeLogin(data);
    }

    throw new Error('Unexpected email verification response from server.');
  };

  const register = async (
    email: string,
    password: string,
    first_name: string,
    last_name: string,
    phone: string
  ) => {
    const {
      data: {
        data: { token, user },
      },
    }: UserSessionResponse = await axios.post('/signup', {
      email,
      password,
      first_name,
      phone,
      user_type: Number(process.env.USER_TYPE),
      last_name,
    });
    setSession(token);
    resetUnauthorizedApiRequestTracking();
    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        pollDashboardDuoChallenge,
        requestDuoEmailFallback,
        verifyDuoEmailFallbackCode,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
